<template>
  <VBtn flat :disabled="isDisabled" @click="showConfirmModal">{{
    isSubscribed ? 'Yes' : 'No'
  }}</VBtn>
</template>

<script lang="ts" setup>
  // Types
  import type { WritableComputedRef } from 'vue';
  import type { ManagerUserModel } from '~/modules/manager/models/user';
  import type { ESubscriptionType } from '~/modules/manager/types';

  // Composables
  import { useNotifyResource } from '~/misc/resource/composables/use-notify-resource';

  // Components
  import VBtn from '~/misc/v-btn';

  const ConfirmModal = defineAsyncComponent(
    () =>
      import(
        '~/modules/manager/company/users/subscription/confirm-modal/SubscriptionConfirmModal.vue'
      )
  );

  const { show: showModal } = useModal();
  const { notifyFailure, notifySuccess } = useNotifyResource();

  const props = defineProps({
    userId: {
      type: Number,
      required: true,
    },
    userName: {
      type: String,
      required: true,
    },
    userEmail: {
      type: String,
      required: true,
    },
    modelValue: {
      type: Array as PropType<ESubscriptionType[]>,
      required: true,
    },
    model: {
      type: Object as PropType<ManagerUserModel>,
      required: true,
    },
    isDisabled: {
      type: Boolean,
      default: false,
    },
    afterSuccessCallback: {
      type: Function as PropType<() => void>,
      default: undefined,
    },
    type: {
      type: String as PropType<ESubscriptionType>,
      required: true,
    },
  });

  const proxyModelValue: WritableComputedRef<ESubscriptionType[]> = computed({
    get(): ESubscriptionType[] {
      return props.modelValue;
    },
    set(value: ESubscriptionType[]): void {
      emit('update:modelValue', value);
    },
  });

  const isSubscribed: ComputedRef<boolean> = computed<boolean>(() =>
    props.modelValue.includes(props.type)
  );

  const emit = defineEmits<{
    (e: 'update:modelValue', value: ESubscriptionType[]): void;
  }>();

  const showConfirmModal = () => {
    const newState: boolean = !isSubscribed.value;

    showModal(ConfirmModal, {
      name: props.userName,
      email: props.userEmail,
      state: newState,
      type: props.type,
      confirmedCallback: () => {
        props.model
          .changeSubscription(props.userId, newState, props.type)
          .then(() => {
            if (newState) {
              proxyModelValue.value = [...proxyModelValue.value, props.type];
            } else {
              proxyModelValue.value = proxyModelValue.value.filter(
                (type: ESubscriptionType) => type !== props.type
              );
            }
            notifySuccess();
            props.afterSuccessCallback?.();
          })
          .catch(() => {
            notifyFailure();
          });
      },
    });
  };
</script>
